import React from 'react'
import { useTheme } from '@/hooks/useTheme'
import { useDeviceDetection } from '@/hooks/useDeviceDetection'
import { useReportEspaciosTop } from '@/hooks/useReports'
import { FiltrosReporte } from '@/services/reportService'
import ReportCard from './ReportCard'
import ReportTable from './ReportTable'
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts'

interface EspaciosTopReportProps {
  filtros: FiltrosReporte
  onRefresh?: () => void
}

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props: any) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="w-full h-96 flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200">
          <div className="text-center">
            <p className="text-gray-600 font-medium">Error al renderizar el gráfico</p>
            <p className="text-sm text-gray-500 mt-2">Intenta actualizar la página</p>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

const EspaciosTopReport: React.FC<EspaciosTopReportProps> = ({ filtros, onRefresh }) => {
  const { colors } = useTheme()
  const { isMobile } = useDeviceDetection()
  const { data: reportData, isLoading, error, refetch } = useReportEspaciosTop(filtros)

  const handleRefresh = () => {
    refetch()
    onRefresh?.()
  }

  const getErrorMessage = () => {
    if (!error) return undefined
    const errorString = error instanceof Error ? error.message : String(error)
    return errorString
  }

  const handleExport = () => {
    if (!reportData?.datos) return

    const csv = [
      ['Código', 'Nombre', 'Tipo', 'Reservas', 'Noches', 'Ingresos'].join(','),
      ...reportData.datos.map((d) =>
        [
          d.codigo,
          d.nombre,
          d.tipo,
          d.reservas,
          d.noches_totales,
          d.ingresos.toFixed(2)
        ].join(',')
      )
    ].join('\n')

    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `espacios-top-${new Date().toISOString().split('T')[0]}.csv`
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  }

  const chartData = (reportData?.datos || []).map((d, index) => ({
    codigo: d.codigo,
    nombre: d.nombre,
    tipo: d.tipo,
    reservas: Number(d.reservas) || 0,
    noches_totales: Number(d.noches_totales) || 0,
    ingresos: Number(d.ingresos) || 0,
    nombre_corto: `E${index + 1}` // Usar índice para evitar duplicados (E1, E2, E3, etc.)
  }))

  const tableColumns = [
    { key: 'codigo' as const, label: 'Código' },
    { key: 'nombre' as const, label: 'Nombre' },
    { key: 'tipo' as const, label: 'Tipo' },
    { key: 'reservas' as const, label: 'Total reservas' },
    { key: 'noches_totales' as const, label: 'Unidades totales' },
    {
      key: 'ingresos' as const,
      label: 'Ingresos',
      render: (value: number) => {
        const validValue = Number.isFinite(value) ? value : 0
        return `$${validValue.toLocaleString('es-CL', { maximumFractionDigits: 0 })}`
      }
    }
  ]

  return (
    <ReportCard
      title="Top 10 Espacios"
      subtitle="Espacios más reservados y rentables"
      loading={isLoading}
      error={getErrorMessage()}
      onExport={handleExport}
      chartComponent={
        <ErrorBoundary>
          <div style={{ width: '100%', height: '100%', minWidth: 0 }}>
            <ResponsiveContainer width="100%" height={isMobile ? 400 : 500}>
              <ComposedChart
                data={chartData}
                margin={isMobile
                  ? { top: 20, right: 10, bottom: 50, left: 10 }
                  : { top: 20, right: 20, bottom: 50, left: 80 }
                }
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="nombre_corto"
                  angle={-45}
                  textAnchor="end"
                  height={70}
                  tick={{ fontSize: 12 }}
                />
                <YAxis
                  yAxisId="left"
                  label={isMobile ? undefined : { value: 'Ingresos ($)', angle: -90, position: 'insideLeft', offset: 0, dx: -50 }}
                  tick={{
                    fontSize: isMobile ? 10 : 12,
                    formatter: (value: number) => isMobile
                      ? `$${(value / 1000).toFixed(0)}k`
                      : value.toLocaleString('es-CL', { maximumFractionDigits: 0 })
                  }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  label={isMobile ? undefined : { value: 'Reservas', angle: 90, position: 'insideRight', offset: 10 }}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    color: colors.primaryDark,
                    borderRadius: '8px',
                    border: `2px solid ${colors.primary}`,
                    padding: '12px',
                    fontSize: '12px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                  }}
                  formatter={(value: any, name: string) => {
                    if (typeof value === 'number') {
                      if (name === 'Ingresos') {
                        const formattedValue = `$${value.toLocaleString('es-CL', { maximumFractionDigits: 0 })}`
                        return [formattedValue, `${name} Total (barra)`]
                      }
                      if (name === 'Reservas') {
                        return [value.toFixed(0), `${name} (línea)`]
                      }
                      return [value.toFixed(0), name]
                    }
                    return [value, name]
                  }}
                  labelFormatter={(label) => {
                    const item = chartData.find(d => d.nombre_corto === label)
                    return item ? `${item.nombre} (${item.tipo})` : label
                  }}
                  wrapperStyle={{ outline: 'none' }}
                />
                <Legend
                  layout={isMobile ? 'horizontal' : 'vertical'}
                  align={isMobile ? 'center' : 'right'}
                  verticalAlign={isMobile ? 'bottom' : 'middle'}
                  wrapperStyle={{ paddingLeft: isMobile ? '0' : '20px', fontSize: isMobile ? '10px' : '12px' }}
                />
                <Bar
                  yAxisId="left"
                  dataKey="ingresos"
                  fill={colors.primary}
                  name="Ingresos"
                  radius={[8, 8, 0, 0]}
                />
                <Line
                  yAxisId="right"
                  type="linear"
                  dataKey="reservas"
                  stroke={colors.primaryDark}
                  strokeWidth={3}
                  name="Reservas"
                  dot={{
                    fill: colors.primaryDark,
                    r: 5
                  }}
                  activeDot={{
                    r: 7,
                    fill: colors.primaryDark
                  }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </ErrorBoundary>
      }
      tableComponent={<ReportTable columns={tableColumns} data={reportData?.datos || []} />}
    >
      {reportData?.datos && reportData.datos.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div
            className="p-4 rounded-lg"
            style={{
              backgroundColor: `${colors.primary}15`,
              borderLeft: `4px solid ${colors.primary}`
            }}
          >
            <p className="text-xs text-gray-600 mb-1">Total espacios</p>
            <p className="text-2xl font-bold" style={{ color: colors.primary }}>
              {reportData.datos.length}
            </p>
          </div>
          <div
            className="p-4 rounded-lg"
            style={{
              backgroundColor: `${colors.primary}15`,
              borderLeft: `4px solid ${colors.primary}`
            }}
          >
            <p className="text-xs text-gray-600 mb-1">Ingresos totales</p>
            <p className="text-2xl font-bold" style={{ color: colors.primary }}>
              ${reportData.datos
                .reduce((sum, d) => sum + d.ingresos, 0)
                .toLocaleString('es-CL', { maximumFractionDigits: 0 })}
            </p>
          </div>
          <div
            className="p-4 rounded-lg"
            style={{
              backgroundColor: `${colors.primary}15`,
              borderLeft: `4px solid ${colors.primary}`
            }}
          >
            <p className="text-xs text-gray-600 mb-1">Reservas totales</p>
            <p className="text-2xl font-bold" style={{ color: colors.primary }}>
              {reportData.datos.reduce((sum, d) => sum + d.reservas, 0)}
            </p>
          </div>
        </div>
      )}
    </ReportCard>
  )
}

export default EspaciosTopReport
